import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { AudioSegment } from "../types/story";
import { useMediaQuery } from "../lib/useMediaQuery";
import { useFitText } from "../lib/useFitText";

/**
 * THE LIVING WORD — the centerpiece of the read-along.
 *
 * Instead of a hard pill snapping word to word, a soft pool of warm light (the
 * "reading lamp") GLIDES across the line with spring easing — it eases in and
 * arrives, never scrolls or snaps. The focus word brightens and swells a hair,
 * then settles; the next word pre-warms a half-beat early (the page inhaling);
 * words just-read cool into settled ink. The line should feel SUNG to the child,
 * not scanned by a machine.
 *
 * Our timing data is sentence-level (`segments`), so per-word motion is derived:
 * each segment's [startMs,endMs] window is shared across its words in proportion
 * to their length. This is purely presentational interpolation of the narration
 * we already have — no content is generated.
 *
 * Graceful degradation:
 *  - no `segments` → the plain `text`, calm and legible (no lamp).
 *  - prefers-reduced-motion → no lamp, no per-word motion: the spoken sentence
 *    gets a calm STATIC warm highlight that still reads beautifully.
 *
 * Accessibility: every word stays full-contrast and legible (a parent often
 * reads ahead of the voice) — state is carried by warmth and the gliding light,
 * never by graying text out. The paragraph keeps its `aria-label`; the visual
 * word-spans are `aria-hidden`.
 */
const SPREAD_QUERY = "(min-width: 820px)";

interface WordTok {
  text: string;
  seg: number;
  idx: number;
}

/** Flatten segments into per-word tokens, preserving (segment, word) coordinates. */
function tokenize(segments: AudioSegment[]): WordTok[] {
  const out: WordTok[] = [];
  segments.forEach((s, seg) => {
    splitWords(s.text).forEach((text, idx) => out.push({ text, seg, idx }));
  });
  return out;
}

function splitWords(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean);
}

/** Sub-windows for each word of a segment, sized by character length. */
function segWordWindows(seg: AudioSegment): { start: number; end: number }[] {
  const weights = splitWords(seg.text).map((w) => w.length + 1);
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  const span = Math.max(1, seg.endMs - seg.startMs);
  let acc = 0;
  return weights.map((wt) => {
    const start = seg.startMs + (acc / total) * span;
    acc += wt;
    return { start, end: seg.startMs + (acc / total) * span };
  });
}

/** Which segment/word is being spoken right now. */
function locate(
  segments: AudioSegment[],
  currentMs: number
): { aSeg: number; focus: number; finished: boolean } {
  let aSeg = -1;
  for (let i = 0; i < segments.length; i++) {
    if (currentMs < segments[i].endMs) {
      aSeg = i;
      break;
    }
  }
  if (aSeg === -1) return { aSeg: segments.length, focus: -1, finished: true };
  const seg = segments[aSeg];
  if (currentMs < seg.startMs) return { aSeg, focus: -1, finished: false };
  const wins = segWordWindows(seg);
  let focus = wins.findIndex((w) => currentMs >= w.start && currentMs < w.end);
  if (focus === -1) focus = wins.length - 1; // hold on the last word at the tail
  return { aSeg, focus, finished: false };
}

interface LampRect {
  x: number;
  y: number;
  w: number;
  h: number;
  on: boolean;
}

const LAMP_OFF: LampRect = { x: 0, y: 0, w: 0, h: 0, on: false };

export function HighlightedText({
  text,
  segments,
  currentMs,
}: {
  text: string;
  segments?: AudioSegment[];
  currentMs: number;
}) {
  const isSpread = useMediaQuery(SPREAD_QUERY);
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const fitRef = useFitText<HTMLParagraphElement>(text, {
    max: isSpread ? 1 : 1.25,
  });

  const hasSegments = !!segments && segments.length > 0;
  const tokens = useMemo(
    () => (hasSegments ? tokenize(segments) : []),
    [hasSegments, segments]
  );
  const { aSeg, focus, finished } = hasSegments
    ? locate(segments, currentMs)
    : { aSeg: 0, focus: -1, finished: false };

  // Global index of the focus word, for positioning the gliding lamp.
  const focusGlobal = useMemo(() => {
    if (!hasSegments || finished || focus < 0) return -1;
    return tokens.findIndex((t) => t.seg === aSeg && t.idx === focus);
  }, [tokens, hasSegments, finished, focus, aSeg]);

  const wordEls = useRef<(HTMLElement | null)[]>([]);
  const [lamp, setLamp] = useState<LampRect>(LAMP_OFF);
  const [nonce, setNonce] = useState(0);

  // Re-measure the lamp's home when the focus word changes (or the box resizes).
  useLayoutEffect(() => {
    if (reduced || !hasSegments) {
      setLamp(LAMP_OFF);
      return;
    }
    const p = fitRef.current;
    const el = focusGlobal >= 0 ? wordEls.current[focusGlobal] : null;
    if (!p || !el) {
      setLamp((l) => (l.on ? { ...l, on: false } : l));
      return;
    }
    const wr = el.getBoundingClientRect();
    const pr = p.getBoundingClientRect();
    setLamp({
      x: wr.left - pr.left - 9,
      y: wr.top - pr.top - 4,
      w: wr.width + 18,
      h: wr.height + 8,
      on: true,
    });
  }, [focusGlobal, reduced, hasSegments, nonce, fitRef]);

  // Bump the nonce when the line reflows (orientation / window / fit rescale).
  useLayoutEffect(() => {
    const p = fitRef.current;
    if (!p) return;
    const ro = new ResizeObserver(() => setNonce((n) => n + 1));
    ro.observe(p);
    if (p.parentElement) ro.observe(p.parentElement);
    return () => ro.disconnect();
  }, [fitRef]);

  if (!hasSegments) {
    return (
      <p ref={fitRef} className="scene-text">
        {text}
      </p>
    );
  }

  return (
    <p ref={fitRef} className="scene-text scene-text--live" aria-label={text}>
      {!reduced && (
        <span
          className={lamp.on ? "reading-lamp reading-lamp--on" : "reading-lamp"}
          aria-hidden="true"
          style={{
            transform: `translate(${lamp.x}px, ${lamp.y}px)`,
            width: `${lamp.w}px`,
            height: `${lamp.h}px`,
          }}
        />
      )}
      {tokens.map((t, i) => {
        const cls = wordClass(t, { aSeg, focus, finished, reduced });
        const isLast = i === tokens.length - 1;
        return (
          <span key={i}>
            <span
              ref={(el) => (wordEls.current[i] = el)}
              className={cls}
              // Capped stagger so a new page's line settles in word order without
              // ever lagging the narration (all words present within ~0.3s).
              style={{ animationDelay: `${Math.min(i, 10) * 28}ms` }}
              aria-hidden="true"
            >
              {t.text}
            </span>
            {isLast ? "" : " "}
          </span>
        );
      })}
    </p>
  );
}

function wordClass(
  t: WordTok,
  s: { aSeg: number; focus: number; finished: boolean; reduced: boolean }
): string {
  const { aSeg, focus, finished, reduced } = s;
  const inActiveSeg = !finished && t.seg === aSeg;

  if (reduced) {
    // Calm static highlight on the whole spoken sentence.
    if (inActiveSeg && focus >= 0) return "word word--say";
    if (finished || t.seg < aSeg) return "word word--read";
    return "word";
  }

  if (finished || t.seg < aSeg) return "word word--read";
  if (t.seg > aSeg || focus < 0) return "word";
  if (t.idx < focus) return "word word--read";
  if (t.idx === focus) return "word word--focus";
  if (t.idx === focus + 1) return "word word--next";
  return "word";
}
